// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

import { BffWebSocketClient, UserCreatedEvent, getSharedBffWebSocket } from '@/services/bffWebSocket';
import { useI18n } from '@/i18n';

type UseRegisterUserParams = {
  apiBaseUrl: string;
  isFocused: boolean;
  forename: string;
  email: string;
  password: string;
  onReset?: () => void;
};

export const useRegisterUser = ({
  apiBaseUrl,
  isFocused,
  forename,
  email,
  password,
  onReset,
}: UseRegisterUserParams) => {
  const { t } = useI18n();
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [socketState, setSocketState] = useState<'connected' | 'disconnected'>('disconnected');
  const requestIdRef = useRef<string | null>(null);
  const socketRef = useRef<BffWebSocketClient | null>(null);

  useEffect(() => {
    const client = getSharedBffWebSocket(apiBaseUrl);
    socketRef.current = client;
    const handleInboundMessage = (message: UserCreatedEvent) => {
      if (message?.type !== 'user.created') return;
      if (!requestIdRef.current || message.requestId !== requestIdRef.current) return;

      if (message.queued) {
        setStatus(t('register.statusQueued'));
        setError(null);
        setLoading(true);
        return;
      }

      setLoading(false);
      if (message.success === false) {
        const errorKey = message.error;
        if (errorKey === 'user.exists') {
          setError(t('register.userExists'));
        } else {
          setError(message.error ?? t('register.createFailed'));
        }
        setStatus(null);
        return;
      }

      setStatus(t('register.statusSuccess'));
      setError(null);
      onReset?.();
      requestIdRef.current = null;
    };

    const unsubscribe = client.onMessage((msg) => handleInboundMessage(msg as UserCreatedEvent));
    const onConn = client.onConnectionChange((state) => setSocketState(state));
    if (isFocused) {
      client.setSubscriptionContext({ roundId: null, tourId: null });
    }
    return () => {
      unsubscribe();
      onConn();
    };
  }, [apiBaseUrl, isFocused, onReset, t]);

  const sendCreateRequest = useCallback(() => {
    if (!forename.trim() || !password) {
      setError(t('register.validationMissing'));
      return;
    }
    if (!socketRef.current) {
      setError(t('register.connectionMissing'));
      return;
    }

    const requestId = generateRequestId();
    requestIdRef.current = requestId;
    setStatus(t('register.statusQueued'));
    setError(null);
    setLoading(true);
    socketRef.current.sendUserCreate({
      forename: forename.trim(),
      password,
      email: email.trim() || undefined,
      requestId,
    });
  }, [email, forename, password, t]);

  return {
    status,
    error,
    loading,
    socketState,
    sendCreateRequest,
  };
};

const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  return `req-${Date.now()}`;
};
